<template>
  <b-overlay :show="isLoading">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <b-table
      id="templates-table"
      responsive
      hover
      show-empty
      ref="templatesTable"
      :fields="fields"
      :items="templates"
      :selectable="!disabled"
      @row-selected="onTemplateSelected"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <digger-loader />
        </div>
      </template>

      <template #cell(selected)="{ index, rowSelected }">
        <b-form-checkbox
          v-model="rowSelected"
          class="cursor-pointer"
          inline
          :disabled="disabled"
          @change="onRowChecked(index, rowSelected)"
        />
      </template>
    </b-table>

    <b-pagination
      v-if="rows > perPage"
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :current-page="currentPage"
      aria-controls="templates-table"
      align="center"
    />

    <p v-if="templates" align="center">
      Showing {{ templates.length }} out of {{ rows }} templates
    </p>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  async mounted() {
    this.fetchTemplates(
      this.currentPage,
      this.perPage,
      this.searchRequiredFiles,
    )
  },

  props: {
    searchRequiredFiles: {
      type: String,
      default: '',
    },

    templateIds: {
      type: Array,
      default: () => [],
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      perPage: 5,
      currentPage: 1,
      fields: ['selected', { key: 'label', label: '' }],

      selectedTemplateIds: this.templateIds,
      prevSelectedIds: [],
    }
  },

  computed: {
    ...mapGetters('template', {
      templates: 'templates',
      templatesMeta: 'templatesMeta',
    }),

    rows() {
      return this.templatesMeta ? this.templatesMeta.total : 0
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      await this.fetchTemplates(
        newCurrentPage,
        this.perPage,
        this.searchRequiredFiles,
      )
    },

    searchRequiredFiles: async function (newSearch) {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        await this.fetchTemplates(1, this.perPage, newSearch)
      }
    },
  },

  methods: {
    ...mapActions('template', {
      getTemplates: 'getTemplates',
    }),

    fetchingTemplates(status) {
      this.isLoading = status
    },

    async fetchTemplates(page, limit, search) {
      this.fetchingTemplates(true)

      await this.getTemplates({
        page,
        limit,
        search,
      }).finally(() => {
        this.fetchingTemplates(false)

        this.selectedTemplateIds.forEach((x) => {
          this.templates.forEach((template, index) => {
            if (template.id === x) this.$refs.templatesTable.selectRow(index)
          })
        })
      })
    },

    onTemplateSelected(templates) {
      if (this.disabled) return

      if (!this.isLoading) {
        const newSelectedIds = templates.map((template) => template.id)
        const prevSelectedIds = Object.values(this.prevSelectedIds)

        const templateId = prevSelectedIds
          .filter((x) => !newSelectedIds.includes(x))
          .concat(newSelectedIds.filter((x) => !prevSelectedIds.includes(x)))[0]

        // IF Adding
        if (newSelectedIds.length > prevSelectedIds.length) {
          if (!this.selectedTemplateIds.includes(templateId))
            this.selectedTemplateIds.push(templateId)
        }
        // ELSE (Removing)
        else {
          this.selectedTemplateIds = this.selectedTemplateIds.filter(
            (x) => x !== templateId,
          )
        }

        this.prevSelectedIds = newSelectedIds

        this.$emit('templateSelected', this.selectedTemplateIds)
      } else {
        // Page Changing
        this.prevSelectedIds = []
      }
    },

    onRowChecked(index, selected) {
      if (this.disabled) return

      if (selected) this.$refs.templatesTable.selectRow(index)
      else this.$refs.templatesTable.unselectRow(index)
    },

    clearSelected() {
      this.$refs.templatesTable.clearSelected()
    },
  },
}
</script>

<style lang="scss" scoped>
/deep/ .cursor-pointer label {
  cursor: pointer;
}

/* Compact the template list inside the Add/Edit Trade modal on small screens. */
@media (max-width: 575.98px) {
  /deep/ #templates-table td,
  /deep/ #templates-table th {
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
  }

  p {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
}
</style>
